<template>
  <div id="contacts-page">
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :sortableColumns="[ 'maincontact',
                          'firstname',
                          'lastname',
                          'email',]"
      :iconHeader="hasPermission('contacts.add') ? 'add' : null"
      :title="$t('contacts.pageName')"
      :isSelectable="hasPermission('contacts.view')"
      :items="contacts"
      :pagination="pagination"
      :headers="tableHeaders"
      :itemActions="actions"
      :isCustomIcon="true"
      :isInfinitScroll="true"
      :centerColumns="['action', 'receiveLabReport', 'receiveServiceReport']"
      :notSortableColumns="['action', 'phoneNumber', 'receiveLabReport', 'receiveServiceReport']"
      hideDefaultFooter
      class="contacts"
      isExportable
      @sendQueryParams="fetchQuery"
      @iconAction="onClickAction"
    >
      <template
        v-slot:item.receiveLabReport="{item}"
      >
        <VCheckbox
          v-model="item.receiveLabReport"
          class="d-flex justify-center"
          :disabled="!hasPermission('contacts.receiveReport')"
          @input="val => onCheckboxChange(val, item, 'receiveLabReport')"
        />
      </template>
      <template v-slot:item.receiveServiceReport="{item}">
        <VCheckbox
          v-model="item.receiveServiceReport"
          class="d-flex justify-center"
          :disabled="!hasPermission('contacts.receiveReport')"
          @input="val => onCheckboxChange(val, item, 'receiveServiceReport')"
        />
      </template>
      <template
        v-slot:item.mainContact="{item}"
      >
        <img
          v-if="item.mainContact"
          src="/dashboard/mainContact.svg"
        >
        <img
          v-if="!item.mainContact"
          src="/dashboard/notMainContact.svg"
        >
      </template>

      <template v-slot:item.firstName="{item}">
        <div class="firstName-container">
          <p style="margin-bottom:2px">
            {{ item.firstName }}
          </p>
          <div
            v-if="item.mainContact"
            class="mainContact-container"
          >
            <p style="white-space: nowrap">
              {{ $t('forms.labels.mainContact') }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:item.phoneNumber="{item}">
        <FormatedPhoneNumber
          :item="item"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import ContactForm from '@/components/forms/ContactForm.vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import perpage from '@/constants/pagination';
import FormatedPhoneNumber from '../../components/shared/FormatPhoneNumber.vue';

export default {
    components: {
        TableRemote,
        VCheckbox,
        FormatedPhoneNumber,
    },
    data() {
        return {
            headers: ['mainContact',
                'firstName',
                'lastName',
                'email',
                'phoneNumber',
                'receiveLabReport',
                'receiveServiceReport'],

            queryParams: {
                perpage,
                order: 'lastName_asc',
                client: this.$route.params.clientId,
                site: this.$route.params.siteId,
                includeSitesContacts: this.includeSitesContacts,
            },
            pagination: {},
            contacts: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        tableHeaders() {
            return this.headers.map(header => ({
                text: !header ? '' : this.$t(`contacts.labels.${header}`),
                value: header,
                isAlreadyTranslated: true,
            }));
        },
        includeSitesContacts() {
            return Object.prototype.hasOwnProperty.call(this.$route.params, 'clientId')
            && Object.prototype.hasOwnProperty.call(this.$route.params, 'siteId');
        },
        actions() {
            const actions = [];
            if (this.hasPermission('contacts.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('contacts.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    created() {
        this.loading = true;
        this.getContacts();
        this.loading = false;
    },
    methods: {
        async fetchQuery(queryParams) {
            if (!this.isInitializing) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    perpage: queryParams?.perpage || queryParams.perpage,
                    search: queryParams?.search || '',
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                /* this.queryParams.page = 1;
                this.queryParams.search = queryParams?.search || ''; */
                this.pagination = {};
                this.contacts = [];
            }
            await this.getContacts();
        },

        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'firstname':
                    newValue = 'firstName';
                    break;
                case 'lastname':
                    newValue = 'lastName';
                    break;
                case 'email':
                    newValue = 'email';
                    break;

                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },


        async getContacts() {
            if (this.queryParams.client && (isEmpty(this.pagination) || this.pagination?.currentPage !== this.queryParams?.page)) {
                this.isInitializing = true;
                const { data } = await this.$rq.getContacts(this.queryParams);
                this.contacts = [...this.contacts, ...data.contacts];
                this.pagination = data.pagination;
                this.isInitializing = false;
            }
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            }
            switch (action?.mode) {
                case 'edit': this.$root.$emit('showModal', {
                    title: this.$t('contacts.editContact'),
                    component: Vue.component('editContactForm', ContactForm),
                    payload: {
                        populateWith: {
                            ...item,
                        },
                        isEditable: true,
                    },
                    onConfirm: async requestPayload => this.updateContact(requestPayload),
                });
                    break;
                case 'delete': this.$root.$emit('showModal', {
                    title: this.$t('contacts.removeContact'),
                    component: Vue.component('confirmContactForm', ConfirmForm),
                    payload: { msg: this.$t('contacts.confirmRemove') },
                    onConfirm: () => this.deleteContact(item),
                    isForm: false,
                }); break;
                default:
            }
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('contacts.addContact'),
                component: Vue.component('addContactForm', ContactForm),
                onConfirm: requestPayload => this.addContactRequest(requestPayload),
            });
        },
        async deleteContact(item) {
            const { id, firstName, lastName } = item;
            await this.$rq.deleteContact(id);

            this.$toasted.global.toast_success({
                message: this.$t('msg.contactDeleted', {
                    data: `${firstName} ${lastName}`,
                }),
            });
            this.fetchQuery({});
        },
        async updateContact(payload) {
            const { id, firstName, lastName } = payload;
            await this.$rq.updateContact(id, payload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.contactModified', {
                    data: `${firstName} ${lastName}`,
                }),
            });
            this.fetchQuery({});
        },
        async addContactRequest(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);

            const { data } = await this.$rq.addContact(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.contactCreated', { contact: `${data.firstName} ${data.lastName}` }),
            });
            this.fetchQuery({});
        },
        async onCheckboxChange(val, { id }, field) {
            const payload = {};
            payload[field] = val;
            await this.$rq.updateContactRecieveReport(id, payload);
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getContacts();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss">
     .contacts th:first-child {
        visibility: hidden;
        max-width:30px
    }
     .contacts td:first-child {
        max-width:30px
    }
    .mainContact-container {
        color:#818181;
        width: 100%;
        position: absolute;
        bottom: 0px;
        right: 0px;
        font-size:smaller;
        font-style: italic
    }
    .firstName-container {
        position: relative;
        height: 100%;
        display: flex;
        align-items: center;
    }
    @media screen and (max-width: 1200px) {
        .contacts td:first-child {
            max-width:40px
        }
    }
</style>
